import { Alert, Pagination } from "@mui/lab";
import { Box, ImageList } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { type Moment } from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getPricingPlans } from "../../apis/rental/city-apis";
import { getRentals } from "../../apis/rental/rental-apis";
import type { PricingPlan } from "../../domain/city";
import type { Rental, RentalListParams } from "../../domain/rental";
import { useCity, useShowToast } from "../../hooks";
import { strings } from "../../I18n";
import { RentalsGalleryItem } from "./RentalsGalleryItem";

const DATE_SEARCH_PARAM_KEY = "date";
const DATE_SEARCH_PARAM_FORMAT = "YYYY-MM-DD";

export const RentalsGallery = () => {
  const { selectedCity, savedCityFullInfo } = useCity();
  const cityId = parseInt(selectedCity);
  const tomorrowRef = useRef<Moment>(moment().add(1, "days"));
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const showToast = useShowToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const dateStr = searchParams.get(DATE_SEARCH_PARAM_KEY);
  const pageStr = searchParams.get("page");

  const updateDate = (value: moment.Moment | null) => {
    if (value) {
      searchParams.set(
        DATE_SEARCH_PARAM_KEY,
        value.format(DATE_SEARCH_PARAM_FORMAT)
      );
      searchParams.delete("page");
    } else {
      searchParams.delete(DATE_SEARCH_PARAM_KEY);
      searchParams.delete("page");
    }

    setSearchParams(searchParams);
  };

  const params = useMemo<RentalListParams>(() => {
    const date = dateStr
      ? moment(dateStr, DATE_SEARCH_PARAM_FORMAT, true)
      : moment();
    const page = pageStr ? Math.max(0, parseInt(pageStr) - 1) : 0;

    return {
      cityId,
      size: 1000,
      page,
      endTimeFrom: date.startOf("day").toISOString(),
      endTimeTo: date.endOf("day").toISOString(),
    };
  }, [cityId, dateStr, pageStr]);

  useEffect(() => {
    if (params.cityId) {
      setLoading(true);
      getRentals(params)
        .then((result) => {
          setRentals(result.content);
          setTotalPages(result.totalPages);
        })
        .catch(() => {
          showToast(strings("message.failedToLoadRentals"), "error");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setRentals([]);
      setTotalPages(0);
    }
  }, [params, showToast]);

  useEffect(() => {
    if (cityId) {
      getPricingPlans(cityId)
        .then((result) => {
          setPlans(result);
        })
        .catch(() => {
          showToast(strings("message.failedToLoadPricingPlans"), "error");
        });
    } else {
      setPlans([]);
    }
  }, [cityId, showToast]);

  const isImagesShown = useMemo(() => {
    return !!savedCityFullInfo && !!rentals.length && !!plans.length;
  }, [savedCityFullInfo, rentals, plans]);

  return (
    <Box sx={{ padding: "20px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <p className="dashboardTitle">{strings("title.rentalsGallery")}</p>

        <DatePicker
          label={strings("descriptions.date")}
          maxDate={tomorrowRef.current}
          value={moment(params.endTimeFrom)}
          onChange={updateDate}
        />
      </Box>

      {!savedCityFullInfo && (
        <Alert severity="warning">
          {strings("message.selectCityForPhotos")}
        </Alert>
      )}
      {!!savedCityFullInfo && !rentals.length && !loading && (
        <Alert severity="info">{strings("message.noRentalsFoundOnDate")}</Alert>
      )}
      {loading && (
        <Alert severity="info">{strings("message.loadingRentals")}</Alert>
      )}

      {isImagesShown && (
        <ImageList cols={6}>
          {rentals.map((rental) => (
            <RentalsGalleryItem
              rental={rental}
              cityCode={savedCityFullInfo?.code ?? ""}
              plans={plans}
              key={rental.id}
            />
          ))}
        </ImageList>
      )}

      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 3,
            mb: 2,
          }}
        >
          <Box sx={{ mb: 1, color: "text.secondary", fontSize: "0.875rem" }}>
            {strings("descriptions.pageOfTotal", {
              page: (params.page || 0) + 1,
              total: totalPages,
            })}
          </Box>
          <Pagination
            count={totalPages}
            page={(params.page || 0) + 1}
            onChange={(_, page) => {
              searchParams.set("page", page.toString());
              setSearchParams(searchParams);
            }}
            color="primary"
            size="large"
            showFirstButton={true}
            showLastButton={true}
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "1rem",
                minWidth: "40px",
                height: "40px",
              },
              "& .Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
